// Partner cockpit — per-tenant drill-in (B5B-PARTNER-COCKPIT §5). One mandated tenant's detail rendered
// inside the cockpit chrome: KPIs + recent open tickets + processing register. The mandate is re-checked
// server-side (403 if out of scope). Behind real auth.
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Ticket, CalendarClock, LayoutGrid, FileStack } from "lucide-react";
import { cockpit, CockpitError } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <span className="text-muted-foreground" aria-hidden>{icon}</span>
      </CardHeader>
      <CardContent><div className="text-2xl font-semibold">{value}</div></CardContent>
    </Card>
  );
}

export function TenantDetailRoute() {
  const { tenantId } = useParams({ from: "/tenant/$tenantId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-drill", tenantId], queryFn: () => cockpit.drillIn(tenantId), retry: false });

  if (error instanceof CockpitError && error.status === 403) {
    return <div className="flex flex-col gap-4"><PageHeader title="Tenant" /><Card><CardContent className="py-8 text-center text-sm text-destructive">This tenant is outside your mandate.</CardContent></Card></div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild size="sm" variant="ghost"><Link to="/"><ArrowLeft className="size-4" /> Portfolio</Link></Button>
      </div>
      <PageHeader title={data?.tenantName ?? "Tenant"} description={data?.readOnly ? "Read-only mandate" : "Mandated tenant"} />
      {isLoading && <Skeleton className="h-24" />}
      {error && !(error instanceof CockpitError && error.status === 403) && <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi icon={<Ticket className="size-4" />} label="Open tickets" value={data.kpis.openTickets} />
            <Kpi icon={<CalendarClock className="size-4" />} label="Due ≤7d" value={data.kpis.dueSoon} />
            <Kpi icon={<LayoutGrid className="size-4" />} label="Programs" value={data.kpis.programs} />
            <Kpi icon={<FileStack className="size-4" />} label="Processings" value={data.kpis.verwerkingen} />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Open tickets</CardTitle></CardHeader>
              <CardContent>
                {data.recentTickets.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
                  <ul className="flex flex-col gap-2">{data.recentTickets.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-3 text-sm"><Link to="/ticket/$ticketId" params={{ ticketId: t.id }} className="truncate hover:underline">{t.title}</Link><Badge variant="secondary">{t.state ? humanize(t.state) : "—"}</Badge></li>
                  ))}</ul>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Processing register</CardTitle></CardHeader>
              <CardContent>
                {data.verwerkingen.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
                  <ul className="flex flex-col gap-2">{data.verwerkingen.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-3 text-sm"><Link to="/verwerking/$verwerkingId" params={{ verwerkingId: v.id }} className="truncate hover:underline">{v.name}</Link><Badge variant="outline">{humanize(v.status)}</Badge></li>
                  ))}</ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
